import React, { useCallback, useEffect, useState } from "react";
import Button from "../forms/Button";
import SprintDocumentCard from "./SprintDocumentCard";
import useSprintDocument from "./useSprintDocument";
import { sprintService, apiErrorMessage } from "../../api/taskBoardService";
import { SectionCard, ErrorState, PriorityBadge, SkeletonRows } from "./shared";
import { STATUS_LABELS } from "./constants";
import SelectCheckbox from "./SelectCheckbox";
import { buildSprintPlanPdf, downloadBlob, fmtDate, syncSprintPlanDocument } from "./sprintDocuments";


const Empty = ({ children }) => <p className="text-sm italic text-faint">{children}</p>;

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-line p-3">
    <p className="text-xs text-faint">{label}</p>
    <p className="text-xl font-semibold text-ink">{value}</p>
  </div>
);

const TASKS_SHOWN = 8;

/** Compact task list: descriptions are collapsed per task and long lists are cut with "Show more". */
const PlanTasks = ({ tasks }) => {
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState(() => new Set());
  if (!tasks.length) return <Empty>No tasks in this sprint.</Empty>;
  const shown = all ? tasks : tasks.slice(0, TASKS_SHOWN);
  const toggle = (id) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <>
      <ol className="space-y-3">
        {shown.map((t) => (
          <li key={t._id} className="text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-ink">
                {t.index}. #{t.number} {t.title}
              </span>
              <PriorityBadge priority={t.priority} />
              <span className="text-xs text-faint">Deadline {fmtDate(t.dueDate)}</span>
              <span className="text-xs text-muted">{STATUS_LABELS[t.status]}</span>
              {t.carriedOverFrom && <span className="pill pill-accent">from {t.carriedOverFrom.name}</span>}
              {t.movedTo && <span className="pill bg-raise text-muted">moved to {t.movedTo.name}</span>}
              {t.descriptionText && (
                <button
                  type="button"
                  onClick={() => toggle(t._id)}
                  aria-expanded={open.has(t._id)}
                  className="text-xs font-medium text-accent-ink hover:underline"
                >
                  {open.has(t._id) ? "Hide details" : "Show details"}
                </button>
              )}
            </div>
            {open.has(t._id) && (
              <p className="mt-1 ml-5 text-muted whitespace-pre-wrap break-words">{t.descriptionText}</p>
            )}
          </li>
        ))}
      </ol>
      {tasks.length > TASKS_SHOWN && (
        <button
          type="button"
          onClick={() => setAll((v) => !v)}
          className="mt-3 text-sm font-medium text-accent-ink hover:underline"
        >
          {all ? "Show less" : `Show more (${tasks.length - TASKS_SHOWN} more)`}
        </button>
      )}
    </>
  );
};

/** Carry-over tasks still linked to completed sprints, offered to this sprint (admins pick explicitly). */
const AvailableCarryOver = ({ sprint, tasks, admin, onMoved, showError, showSuccess }) => {
  const [selected, setSelected] = useState(() => new Set());
  const [moving, setMoving] = useState(false);
  if (!tasks.length) return null;

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const move = async () => {
    setMoving(true);
    try {
      const res = await sprintService.assignTasks(sprint._id, [...selected]);
      const failed = res.data.filter((r) => !r.ok);
      if (failed.length) showError(`${failed.length} task(s) could not be moved: ${failed[0].message}`);
      else showSuccess(`${res.data.length} task(s) added to ${sprint.name}`);
      setSelected(new Set());
      onMoved();
    } catch (err) {
      showError(apiErrorMessage(err, "Could not move the tasks"));
    } finally {
      setMoving(false);
    }
  };

  return (
    <SectionCard
      title={`Unfinished in completed sprints (${tasks.length})`}
      actions={
        admin && (
          <Button size="sm" onClick={move} disabled={!selected.size || moving}>
            {moving ? "Adding…" : `Add to this sprint${selected.size ? ` (${selected.size})` : ""}`}
          </Button>
        )
      }
    >
      <p className="text-xs text-faint mb-2">
        These stay linked to their original sprint until you add them to a sprint. Nothing moves automatically.
      </p>
      {admin && (
        <SelectCheckbox
          checked={selected.size === tasks.length}
          indeterminate={selected.size > 0}
          onChange={() => setSelected(selected.size === tasks.length ? new Set() : new Set(tasks.map((t) => t._id)))}
          label="Select all carry-over tasks"
          className="mb-1 text-xs text-muted"
        >
          Select all
        </SelectCheckbox>
      )}
      <ul className="divide-y divide-line">
        {tasks.map((t) => (
          <li
            key={t._id}
            className={`flex items-center gap-2 py-2 px-1.5 -mx-1.5 rounded-lg text-sm transition-colors ${
              selected.has(t._id) ? "bg-accent-soft" : ""
            }`}
          >
            {admin && (
              <SelectCheckbox
                checked={selected.has(t._id)}
                onChange={() => toggle(t._id)}
                label={`Select task #${t.number}`}
              />
            )}
            <span className="font-mono text-[11px] text-faint">#{t.number}</span>
            <span className="flex-1 min-w-0 break-words text-ink">{t.title}</span>
            <span className="text-xs text-faint shrink-0">{STATUS_LABELS[t.status]}</span>
            <span className="text-xs text-muted shrink-0">from {t.originalSprint.name}</span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
};

/**
 * Sprint Plan tab. Everything shown is computed by the API from the task data
 * on every load (and reloads on realtime changes). The PDF is built from that
 * same data, saved to the sprint's attachments, and flagged "outdated" as
 * soon as the data differs from what the saved PDF was made from.
 */
const SprintPlanTab = ({ sprint, admin, refreshKey, onSprintChange, showError, showSuccess }) => {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setPlan((await sprintService.plan(sprint._id)).data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load the sprint plan"));
    }
  }, [sprint._id]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const { busy, genError, regenerate } = useSprintDocument({
    admin,
    hash: plan?.hash,
    document: plan?.document,
    sync: () => syncSprintPlanDocument(sprint),
    onSaved: onSprintChange,
    onDone: load,
    showError,
    showSuccess,
    successMessage: "Sprint plan PDF saved to Documents",
  });

  const preview = async () => {
    try {
      const fresh = (await sprintService.plan(sprint._id)).data;
      downloadBlob(buildSprintPlanPdf(fresh), `${sprint.name.replace(/[^\w-]+/g, "-")}_Sprint-Plan.pdf`);
    } catch (err) {
      showError(apiErrorMessage(err, "Could not generate the PDF"));
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!plan) return <SkeletonRows rows={4} />;
  const { summary, document: doc } = plan;

  return (
    <div className="space-y-4">
      <SprintDocumentCard
        title="Sprint plan document"
        doc={doc}
        busy={busy}
        genError={genError}
        admin={admin}
        onDownload={preview}
        onRegenerate={regenerate}
        emptyText="No PDF saved yet."
      />

      <SectionCard title={plan.sprint.name}>
        <p className="text-sm text-muted">
          {fmtDate(plan.sprint.startDate)} – {fmtDate(plan.sprint.endDate)}
        </p>
        <p className="mt-2 text-sm text-ink whitespace-pre-wrap break-words">{plan.sprint.goal || <Empty>No goal set</Empty>}</p>
        <div className={`mt-4 grid grid-cols-2 gap-2 ${plan.carryOverToNextFinal ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
          <Stat label="Total tasks" value={summary.totalTasks} />
          <Stat label="Completed" value={`${summary.completedTasks} (${summary.completionRate}%)`} />
          <Stat label="Carry-over from previous" value={summary.carryOverFromCount} />
          {plan.carryOverToNextFinal && <Stat label="Carry-over to next" value={summary.carryOverToNextCount} />}
        </div>
      </SectionCard>

      <SectionCard title={`Tasks (${plan.tasks.length})`}>
        <PlanTasks tasks={plan.tasks} />
      </SectionCard>

      <SectionCard title="Blockers">
        {plan.retrospectiveBlockers ? (
          <p className="text-sm text-ink whitespace-pre-wrap break-words">{plan.retrospectiveBlockers}</p>
        ) : (
          <Empty>No blockers recorded. They are entered in the Retrospective tab.</Empty>
        )}
      </SectionCard>

      <SectionCard title={`Carry-over from previous sprint (${plan.carryOverFromPrevious.length})`}>
        {plan.carryOverFromPrevious.length ? (
          <ul className="space-y-1 text-sm">
            {plan.carryOverFromPrevious.map((t) => (
              <li key={t._id}>
                <span className="font-mono text-[11px] text-faint">#{t.number}</span> {t.title} — from <span className="text-muted">{t.fromSprint.name}</span>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Nothing carried over into this sprint.</Empty>
        )}
      </SectionCard>

      {/* Only meaningful once the sprint is over; before that these are just its open tasks. */}
      {plan.carryOverToNextFinal && (
      <SectionCard title={`Carry-over to next sprint (${plan.carryOverToNext.length})`}>
        {plan.carryOverToNext.length ? (
          <ul className="space-y-1 text-sm">
            {plan.carryOverToNext.map((t) => (
              <li key={t._id}>
                <span className="font-mono text-[11px] text-faint">#{t.number}</span> {t.title} — <span className="text-muted">{STATUS_LABELS[t.status]}</span>
                {t.movedTo && <span className="text-faint"> (moved to {t.movedTo.name})</span>}
              </li>
            ))}
          </ul>
        ) : (
          <Empty>No unfinished tasks.</Empty>
        )}
      </SectionCard>
      )}

      <AvailableCarryOver
        key={plan.carryOverAvailable.map((t) => t._id).join()}
        sprint={sprint}
        tasks={plan.carryOverAvailable}
        admin={admin}
        onMoved={load}
        showError={showError}
        showSuccess={showSuccess}
      />
    </div>
  );
};

export default SprintPlanTab;
